import { Component, effect, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { AssociatedClient, TestUserInput } from '../../../api/types';
import {
  SynButtonComponent,
  SynCheckboxComponent,
  SynComboboxComponent,
  SynergyFormsModule,
  SynIconComponent,
  SynInputComponent,
  SynOptionComponent, SynSelectComponent,
  SynTextareaComponent,
  SynValidateComponent,
} from '@synergy-design-system/angular';
import { FormsModule, NgForm } from '@angular/forms';
import { PasswordInputComponent } from '../../components/password-input.component';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AddAttributeDialogComponent } from './add-attribute-dialog.component';
import { HelpTextComponent } from '../../components/help-text.component';
import { StateService } from '../../../api/state';

@Component({
  selector: 'app-test-user-form',
  template: `
    <form #form=ngForm (submit)="onSubmit(form)">
      <div class="flex-col gap-2 form-width-large">
        <app-help-text>
          <syn-validate on="syn-blur live">
            <syn-input
              label="First Name" i18n-label="@@first-name"
              [(ngModel)]="this.user().firstName"
              [required]="true"
              [maxlength]="maxPropertyLength"
              name="firstName"
              data-e2e="input-first-name"
            />
          </syn-validate>
          <span helpText i18n="@@first-name-help">
            First name of the user.
          </span>
        </app-help-text>
        <app-help-text>
          <syn-validate on="syn-blur live">
            <syn-input
              label="Last Name" i18n-label="@@last-name"
              [(ngModel)]="this.user().lastName"
              [required]="true"
              [maxlength]="maxPropertyLength"
              name="lastName"
              data-e2e="input-last-name" />
          </syn-validate>
          <span helpText i18n="@@last-name-help">
            Last name of the user.
          </span>
        </app-help-text>

        @if (create()) {
          <div class="flex-row gap-1 align-center">
            <syn-input
              label="Name"
              i18n-label="@@name"
              [minlength]="1"
              [maxlength]="100"
              [required]="true"
              [(ngModel)]="usernameName"
              name="usernameName"
              data-e2e="input-username-name"
            />
            <span style="margin-top: auto; font-size: var(--syn-font-size-large)">.</span>
            <syn-combobox
              label="Application" i18n-label="@@application"
              class="flex-grow-1"
              [required]="true"
              [(ngModel)]="usernameApplication"
              (synChangeEvent)="onChangeApplication()"
              name="usernameApplication"
              data-e2e="input-application"
            >
              @for (client of clients(); track client.id) {
                <syn-option>{{ client.clientId }}</syn-option>
              }

            </syn-combobox>
            <app-help-text>
              <syn-input
                [readonly]="true"
                label="Suffix" i18n-label="@@suffix"
                value="@sick.test"
                style="width: 105px"
              />
              <span helpText i18n="@@test-user-name-gen-help">
                The username of the test user will be generated following this pattern.
                You can choose an existing client or a custom string as the application.
              </span>
            </app-help-text>
          </div>
        } @else {
          <app-help-text>
            <syn-validate on="syn-blur live">
              <syn-input
                label="Username" i18n-label="@@username"
                [(ngModel)]="this.user().username"
                [required]="true"
                [maxlength]="maxPropertyLength"
                name="username" />
            </syn-validate>
            <span helpText i18n="@@username-help">
              The username is part of the login credentials.
              It does not need to be a valid email address.
            </span>
          </app-help-text>
        }

        <app-help-text>
          <syn-validate on="syn-blur live">
            <syn-input
              label="Email" i18n-label="@@email"
              [(ngModel)]="this.user().email"
              [disabled]="create()"
              [required]="true"
              [maxlength]="maxPropertyLength"
              type="email"
              name="email" />
          </syn-validate>
          <span helpText i18n="@@test-user-email-help">
            The email address will be used to send verification and reset credentials emails.
            By default, the email address of the test user's creator is used.
          </span>
        </app-help-text>

        <div class="flex-row gap-2">
          <syn-checkbox [(ngModel)]="this.user().enabled" name="enabled">
            <span i18n="@@enabled">Enabled</span>
          </syn-checkbox>
          <syn-checkbox [(ngModel)]="this.user().emailVerified" name="emailVerified">
            <span i18n="@@email-verified">Email Verified</span>
          </syn-checkbox>
          <app-help-text>
            <syn-checkbox [(ngModel)]="this.user().addToSickEmployeeGroup" name="addToSickEmployeeGroup"
            >
              <span i18n="@@sick-employee">SICK Employee</span>
            </syn-checkbox>
            <span helpText i18n="@@test-user-sick-employee-help">
              Determines whether the test user is considered to by a SICK employee.
            </span>
          </app-help-text>
        </div>

        @if (create()) {
          <app-password-input
            label="Password" i18n-label="@@password"
            helpText="Set a password for the user."
            i18n-helpText="@@password-help"
            [(password)]="password"
          />
        }

        <app-help-text>
          <syn-select
            [clearable]="true"
            [multiple]="true"
            [(ngModel)]="associatedClients"
            name="associatedClients"
            label="Associated Clients"
            i18n-label="@@associated-clients"
          >
            @for (client of clients(); track client.id) {
              <syn-option [value]="client.id">{{ client.clientId }}</syn-option>
            }

          </syn-select>
          <span helpText i18n="@@test-user-associated-clients-help">
            By associating clients with the test user, you can easily find all
            test users associated with a client.
          </span>
        </app-help-text>

        <app-help-text>
          <syn-textarea
            label="Notes" i18n-label="@@notes"
            [rows]="4"
            [spellcheck]="false"
            [maxlength]="500"
            [(ngModel)]="this.user().note"
            name="note"
          />
          <span helpText i18n="@@notes-help">
            Add free form notes to this account, e.g. why it was created.
          </span>
        </app-help-text>

        <syn-button
          variant="filled"
          type="submit"
          [disabled]="!canSave(form)"
          data-e2e="btn-submit-test-user"
        >
          <syn-icon [name]="submitIcon() + '_fill'" slot="prefix" />
          <span>{{ submitText() }}</span>
        </syn-button>
      </div>
    </form>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynInputComponent,
    SynCheckboxComponent,
    SynergyFormsModule,
    FormsModule,
    SynTextareaComponent,
    PasswordInputComponent,
    SynValidateComponent,
    FormsModule,
    SynInputComponent,
    SynValidateComponent,
    SynergyFormsModule,
    SynButtonComponent,
    SynIconComponent,
    SynComboboxComponent,
    SynOptionComponent,
    SynSelectComponent,
    HelpTextComponent,
  ],
})
export class TestUserFormComponent implements OnInit {
  location = inject(Location);
  router = inject(Router);
  activeRoute = inject(ActivatedRoute);

  user = input.required<TestUserInput>();
  associatedClients = signal<string[]>([]);

  create = input(false);

  submitIcon = input('');
  submitText = input('');
  submitUser = output<TestUserInput>();

  password = signal('');
  @ViewChild(PasswordInputComponent) passwordInput?: PasswordInputComponent;
  @ViewChild(AddAttributeDialogComponent) addAttributeDialog!: AddAttributeDialogComponent;

  usernameName = signal('');
  usernameApplication = signal('');

  // We have configured a maximum length of 150 characters for first name, last name, email, username
  // If we change the configuration, we might want to change this value here as well.
  maxPropertyLength = 150;

  state = inject(StateService);
  clients = this.state.clients;
  clientUUID = signal('');

  constructor() {
    effect(() => {
      const uuid = this.clientUUID();
      if (uuid.length === 0) return;

      const clients = this.clients();
      for (const client of clients) {
        if (client.id === uuid) {
          this.usernameApplication.set(client.clientId);
          this.associatedClients.set([client.id]);
          break;
        }
      }
    });
  }


  ngOnInit(): void {
    const application = this.activeRoute.snapshot.queryParams['application'] ?? '';
    if (typeof application === 'string' && application.length > 0) {
      this.clientUUID.set(application);
    }

    this.updateAssociatedClients();
    this.state.queryClients(() => this.updateAssociatedClients());
  }

  updateAssociatedClients() {
    const associatedClientUUIDs = this.user().associatedClients.map(
      (client) => client.id);
    this.associatedClients.set(associatedClientUUIDs);
  }

  onSubmit(form: NgForm) {
    if (form.invalid) return;

    const user = this.user();
    if (this.password().length > 0) {
      user.credentials = {
        password: this.password(),
        // The passwords for test users do not need to be temporary
        // The creator wants to create a new test user and immediately know the credentials
        temporary: false,
      };
    }

    const clientIdMap: Record<string, string> = {};
    for (const client of this.clients()) {
      clientIdMap[client.id] = client.clientId;
    }
    user.associatedClients = this.associatedClients().map((id): AssociatedClient => {
      const clientId = clientIdMap[id];
      return { id, clientId };
    });

    if (this.create()) {
      user.username = this.usernameName() + '.' + this.usernameApplication() + '@sick.test';
    }

    this.submitUser.emit(user);
    form.form.markAsPristine();
  }

  canSave(form: NgForm) {
    if (!form.dirty) return false;
    if (form.invalid) return false;

    // Only if a password is set: The password needs to be valid to create a new user
    if (this.passwordInput && this.password().length > 0) {
      if (!this.passwordInput.isValid()) return false;
    }

    return true;
  }

  onChangeApplication() {
    // Add application to associated clients if it is a valid client ID
    const app = this.usernameApplication();
    const foundClient = this.clients().find((client) => client.clientId === app);
    if (!foundClient) return;
    this.associatedClients.update((old) => {
      if (old.includes(foundClient.id)) return old;
      return [...old, foundClient.id];
    });
  }
}
